import { useCallback, useEffect, useMemo } from 'react'
import * as React from 'react'

import { CompactCardAndPinLayout, CompactCardLayout } from './common'

import useToggle from 'hooks/useToggle'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { getItemPinBadge } from 'tripPlanner/utils/tripItemPin'
import ItemPin from '../CleanCard/common/ItemPin'
import { getItemPinCategory } from '../CleanCard/common/itemPinTypes'
import CornerControls from '../DetailItemCard/common/CornerControls'
import type { DetailItemCardVariant } from '../DetailItemCard/common/types'
import { startPlaceOfTripItem } from '../TripMaps/utils'
import TripItemInlineNoteEdit from '../ItemCard/TripItemInlineNoteEdit'
import ItemTitle from '../DetailItemCard/common/ItemTitle'
import HoverOnlyRegenerateItemButton from 'tripPlanner/components/CleanCard/common/HoverOnlyRegenerateItemButton'

interface Props {
  item: TripItem
  icon: React.ReactNode
  title: React.ReactNode
  description?: string
  /** Whether this is the primary card for this item (i.e. the start card, rather than an intermediary or end card) */
  isPrimary?: boolean
  variant: DetailItemCardVariant
}

function BaseCompactCard({ item, title, isPrimary, variant }: Props) {
  const [inFocus, , setInFocusOn, setInFocusOff] = useToggle(false)
  const [showComment, , setShowCommentOn, setShowCommentOff] = useToggle(!!item.notes)
  const { data: trip } = useTrip({ tripId: item.tripId })

  const { off: addNoteModeOff } = useToggle()

  useEffect(() => {
    addNoteModeOff()
    // Clear add note mode any time the note changes
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [item.notes])

  const place = startPlaceOfTripItem(item)

  const { itemNumber, itemColor } = useMemo(() => {
    if (place?.type === 'GEO' && place.point) {
      return getItemPinBadge(item, variant, trip)
    }
    return {
      itemNumber: undefined,
      itemColor: undefined,
    }
  }, [item, variant, trip, place])

  const onRequestAddComment = useCallback(() => {
    setShowCommentOn()
    setInFocusOn()
  }, [setShowCommentOn, setInFocusOn])

  const onNotesChange = useCallback((notes: string | undefined) => {
    if (notes) {
      setShowCommentOn()
    } else {
      setShowCommentOff()
    }
  }, [setShowCommentOn, setShowCommentOff])

  return (
    <CompactCardAndPinLayout>
      {isPrimary && <CompactCardAndPinLayout.Pin className="pin-anchor">
        <ItemPin
          variant={variant}
          category={getItemPinCategory(item.type)}
          itemNumber={itemNumber}
          dayColor={itemColor}
        />
      </CompactCardAndPinLayout.Pin>}
      <CompactCardAndPinLayout.CardContainer>
        <CompactCardLayout.Content>
          {isPrimary && (
            <>
              <HoverOnlyRegenerateItemButton item={item} />
              <CornerControls item={item} onRequestAddComment={onRequestAddComment} />
            </>
          )}
          <ItemTitle>{title}</ItemTitle>
        </CompactCardLayout.Content>
        {isPrimary && showComment && (
          <CompactCardLayout.Comment>
            <TripItemInlineNoteEdit
              item={item}
              inputAlwaysVisible
              inFocus={inFocus}
              onFocus={setInFocusOn}
              onBlur={setInFocusOff}
              onNotesChange={onNotesChange}
            />
          </CompactCardLayout.Comment>
        )}
      </CompactCardAndPinLayout.CardContainer>
    </CompactCardAndPinLayout>
  )
}

export default BaseCompactCard
